import { Box, Button, Skeleton, styled } from '@mui/material';
import { UIEvent, useCallback } from 'react';
import { BodyText } from '@/components/BodyText';
import { FailureKind, useGetRecentFailures } from '@/hooks/api/assets/useGetRecentFailures';
import { FailureRow } from './FailureRow';

// The list stays a fixed size inside the tile; it scrolls and loads more as it nears the
// bottom, rather than growing the page without bound.
const ScrollArea = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  maxHeight: '280px',
  overflowY: 'auto',
});

const LOAD_MORE_THRESHOLD_PX = 48;

export const FailureKindList = ({ kind }: { kind: FailureKind }) => {
  const { failures, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetRecentFailures(kind);
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Deliberately a scroll position check rather than an IntersectionObserver: an observer
  // attached before the tile's styles settle reads the end of the list as already visible
  // and pulls every page at once.
  const loadMoreWhenNearBottom = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX) loadMore();
    },
    [loadMore]
  );

  if (error) return <BodyText color='text.secondary'>Failed to load these failures.</BodyText>;
  if (isLoading) return <Skeleton variant='rounded' height={96} />;

  return (
    <Box display='flex' flexDirection='column' gap='4px' data-testid={`failure-kind-list-${kind}`}>
      <ScrollArea onScroll={loadMoreWhenNearBottom}>
        {(failures ?? []).map(failure => (
          <FailureRow key={failure.id} failure={failure} />
        ))}
        {isFetchingNextPage && <Skeleton variant='rounded' height={32} />}
      </ScrollArea>
      {hasNextPage && !isFetchingNextPage && (
        <Button size='small' color='secondary' onClick={loadMore} sx={{ alignSelf: 'flex-start' }}>
          Load more
        </Button>
      )}
    </Box>
  );
};
